import { PersistentCacheInterface } from '@kaiizen/react-persistent-cache';
import { useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useAuth } from '../../../useAuth';

import { SYNC_RECORD_MAX_AGE_MS, useSyncPersistence } from './SyncPersistenceContext';
import { getBlankSyncTypeStatus, getSyncTypeStatus, SyncRecordBase, SyncTypeStatus } from './SyncRecordTypes';

const isExpiredError = (record: SyncRecordBase): boolean => record.firstErrorAt != null && Date.now() - record.firstErrorAt > SYNC_RECORD_MAX_AGE_MS;

/**
 * `scanVideo` is KaiPro-only: phone scope videos recorded offline (the web
 * uploads videos online and never queues them).
 */
export type SyncType = 'scanData' | 'scanNote' | 'scanPhoto' | 'scanVideo';

// *************************************************************
// Key Helpers
// *************************************************************

export const makeSyncKey = (type: string, userId: string, id: string) => `${type}:${userId}:${id}`;

// Sort oldest-updated-first so recently-failed records (which get a fresh updatedAt)
// drift to the back of the queue and don't starve other pending records.
export const sortSyncRecordsOldestFirst = <T extends Pick<SyncRecordBase, 'updatedAt' | 'createdAt'>>(records: T[]): T[] =>
  [...records].sort((a, b) => (a.updatedAt ?? a.createdAt ?? 0) - (b.updatedAt ?? b.createdAt ?? 0));

export const getSyncPrefix = (type: string, userId: string) => `${type}:${userId}:`;

/**
 * Creates a sync model for managing records in the sync queue.
 *
 * **Synced-record cleanup:** Records with `isSynced === true` are immediately
 * removed from persistent storage. Any write (`put`, `update`, `bulkUpdate`)
 * that produces a synced record will purge it instead of persisting it, and
 * any read (`getAll`) will filter out and purge any stale synced records that
 * may remain from before this cleanup was added. This keeps the sync queue
 * lean — once a record has been confirmed by the server, there is no reason
 * to keep it in the local database.
 */
export const createSyncModel = <RecordType extends SyncRecordBase>(
  cache: PersistentCacheInterface,
  type: SyncType,
  userId: string,
) => {
  const prefix = getSyncPrefix(type, userId);
  const getExpiresAt = () => Date.now() + SYNC_RECORD_MAX_AGE_MS;
  const makeKey = (id: string) => makeSyncKey(type, userId, id);

  /** Purges the record from storage and returns false if the record was purged. */
  const purge = async (id: string): Promise<boolean> => {
    await cache.purge(makeKey(id));
    return true;
  };

  const has = async (id: string): Promise<boolean> => {
    return cache.has(makeKey(id));
  };

  const splitSyncedAndUnsynced = async (records: RecordType[]): Promise<{ synced: RecordType[]; unsynced: RecordType[] }> => {
    const synced: RecordType[] = [];
    const unsynced: RecordType[] = [];
    for (const record of records) {
      if (record.isSynced) {
        synced.push(record);
      } else {
        unsynced.push(record);
      }
    }
    return { synced, unsynced };
  };

  const get = async (id: string): Promise<RecordType> => {
    const value = (await cache.get(makeKey(id))) as RecordType | undefined;
    if (!value || value.isSynced) {
      const err = new Error(`Record not found: ${id}`) as any;
      err.status = 404;
      throw err;
    }
    return value;
  };

  /** If the record is already synced, it is purged rather than stored. returns true if the record was purged.*/
  const put = async (record: RecordType): Promise<boolean> => {
    const now = Date.now();
    if (record.isSynced) {
      return purge(record._id);
    } else {
      const stored: RecordType = {
        ...record,
        createdAt: (record as any).createdAt ?? now,
        updatedAt: now,
      };
      await cache.set(makeKey(record._id), stored, getExpiresAt(), stored.createdAt, now);
      return true;
    }
  };

  const bulkPurge = async (records: RecordType[]): Promise<void> => {
    await Promise.all(records.map(r => purge(r._id)));
  };

  const getAll = async (filter?: (record: RecordType) => boolean): Promise<RecordType[]> => {
    const entries = await cache.getAllEntries(prefix);
    const records = entries.map(e => e.value as RecordType).filter((v): v is RecordType => !!v);

    const { synced, unsynced } = await splitSyncedAndUnsynced(records);
    if (synced.length > 0) {
      await bulkPurge(synced);
    }

    const expired = unsynced.filter(isExpiredError);
    const active = expired.length > 0 ? unsynced.filter(r => !isExpiredError(r)) : unsynced;
    if (expired.length > 0) {
      for (const record of expired) {
        console.error(
          `Purging expired sync record (type: ${type}, id: ${record._id}, scanId: ${record.scanId}, firstErrorAt: ${record.firstErrorAt}, errorMessage: ${record.errorMessage})`,
        );
      }
      await bulkPurge(expired);
    }

    return filter ? active.filter(filter) : active;
  };

  /** If the updated record is synced, purge it from storage instead of persisting. Returns true if the record still needs syncing.*/
  const update = async (recordId: string, updates: Record<string, any> | ((record: RecordType) => Record<string, any>)): Promise<boolean> => {
    const current = await get(recordId);
    const patch = typeof updates === 'function' ? updates(current) : updates;
    return put({ ...current, ...patch, _id: recordId, updatedAt: Date.now() });
  };

  const bulkPut = async (docs: RecordType[]): Promise<boolean[]> => {
    const now = Date.now();
    const updated = docs.map(record => ({ ...record, updatedAt: now })) as RecordType[];
    return Promise.all(updated.map(put));
  };

  const deleteAll = async (): Promise<void> => {
    await cache.purgeAllEntries(prefix);
  };

  const getAllPending = () => getAll(record => record.userId === userId && !record.isSynced && !record.isSyncing && !record.isFailed);

  return {
    has,
    get,
    getAll,
    put,
    getType: () => type,

    /** Subscribe to changes in this collection. On purge events (e.g. markSynced), record will be undefined. */
    subscribe: (callback: (key: string, record: RecordType | undefined) => void): () => void =>
      cache.subscribeToAll((key, record) => {
        if (key.startsWith(prefix)) {
          callback(key, record.value as RecordType | undefined);
        }
      }),

    // Get pending records for the current user, sorted by least-recently updated first
    // This is important so we don't starve syncing on other syncs that are already running or need a retry
    getPending: async () => sortSyncRecordsOldestFirst(await getAllPending()),
    getPendingCount: async () => (await getAllPending()).length,

    /**
     * Prep for a new sync run. All isSyncing=true records are reset to isSyncing=false
     * @returns The updated records
     */
    resetSyncStatus: async () => {
      const result = await getAll(record => record.userId === userId && record.isSyncing);
      if (result.length === 0) return [];
      return bulkPut(result.map(item => ({ ...item, isSyncing: false })));
    },

    markSyncing: async (itemId: string): Promise<boolean> => update(itemId, { isSyncing: true, isFailed: false }),

    /** Marks the record as synced and immediately purges it from persistent storage. */
    markSynced: async (itemId: string): Promise<boolean> => purge(itemId),

    markHardFailed: async (itemId: string, errorMessage?: string) =>
      update(itemId, current => ({
        retryCount: (current.retryCount ?? 0) + 1,
        firstErrorAt: current.firstErrorAt ?? Date.now(),
        isSynced: false,
        isSyncing: false,
        isFailed: true,
        errorMessage,
      })),

    markSoftFailed: async (itemId: string, errorMessage?: string) =>
      update(itemId, current => ({
        retryCount: (current.retryCount ?? 0) + 1,
        firstErrorAt: current.firstErrorAt ?? Date.now(),
        isSynced: false,
        isSyncing: false,
        isFailed: false,
        errorMessage,
      })),

    deleteAndLoseAllUnsyncedData: deleteAll,
  };
};

export type SyncModel<RecordType extends SyncRecordBase> = ReturnType<typeof createSyncModel<RecordType>>;

export const useSyncModel = <RecordType extends SyncRecordBase>(type: SyncType) => {
  const cache = useSyncPersistence();
  const { userId } = useAuth();

  return useMemo(
    () => createSyncModel<RecordType>(cache, type, userId ?? ''),
    [cache, type, userId],
  );
};

export const useSyncRecordStatusSubscription = <RecordType extends SyncRecordBase>(model: SyncModel<RecordType>, scanId?: string) => {
  const [status, setStatus] = useState<SyncTypeStatus>(getBlankSyncTypeStatus());

  const debouncedUpdateSyncRecords = useDebouncedCallback(
    async () => setStatus(getSyncTypeStatus(await model.getAll(scanId ? record => record.scanId === scanId : undefined), 0)),
    100,
  );

  useEffect(() => {
    debouncedUpdateSyncRecords();
    return model.subscribe(() => debouncedUpdateSyncRecords());
  }, [model, scanId]);

  return status;
};
